import React, { useEffect, useRef } from 'react'
import { Select, Switch } from 'antd'
import { DeleteOutlined, CopyOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons'
import { RESPONSE_TYPES, getResponseTypeLabel } from './templates'
import {
  QuestionCardWrapper,
  QuestionCardHeader,
  QuestionNumber,
  QuestionInput,
  ValidationMessage,
  OptionRow,
  OptionInput,
  IconButton,
  CardFooter,
  CardActionsLeft,
  RequiredToggle,
  RequiredLabel,
  AddOptionButton,
  Divider,
} from './style'

const QuestionCard = ({
  question,
  index,
  onUpdate,
  onDelete,
  onDuplicate,
  isCustom = false,
  showValidation = false,
  isFormDisabled = false,
}) => {
  const handleQuestionChange = (e) => {
    onUpdate({ ...question, question: e.target.value })
  }

  const textareaRef = useRef(null)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height =
        `${textareaRef.current.scrollHeight}px`
    }
  }, [question.question])

  const handleTypeChange = (value) => {
    const needsOptions = ['MULTIPLE_CHOICE', 'CHECKBOXES', 'DROPDOWN'].includes(value)
    onUpdate({
      ...question,
      responseType: value,
      options: needsOptions && (!question.options || question.options.length === 0)
        ? ['Option 1']
        : needsOptions
          ? question.options
          : [],
    })
  }

  const handleRequiredToggle = (checked) => {
    onUpdate({ ...question, required: checked })
  }

  const handleOptionChange = (optIndex, value) => {
    const newOptions = [...question.options]
    newOptions[optIndex] = value
    onUpdate({ ...question, options: newOptions })
  }

  const handleAddOption = () => {
    const newOptions = [...(question.options || []), `Option ${(question.options?.length || 0) + 1}`]
    onUpdate({ ...question, options: newOptions })
  }

  const handleRemoveOption = (optIndex) => {
    const newOptions = question.options.filter((_, i) => i !== optIndex)
    onUpdate({ ...question, options: newOptions })
  }

  const showOptions = ['MULTIPLE_CHOICE', 'CHECKBOXES', 'DROPDOWN'].includes(question.responseType)
  const hasValidOptions =
    Array.isArray(question.options) &&
    question.options.some((opt) => typeof opt === 'string' && opt.trim() !== '')
  const showOptionsError = showValidation && showOptions && !hasValidOptions

  return (
    <QuestionCardWrapper>
      <QuestionCardHeader>
        <QuestionNumber>{index + 1}.</QuestionNumber>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <QuestionInput
            ref={textareaRef}
            value={question.question}
            onChange={(e) => {
              e.target.style.height = 'auto'
              e.target.style.height = `${e.target.scrollHeight}px`

              handleQuestionChange(e)
            }}
            placeholder="Enter question"
            disabled={isFormDisabled}
            rows={1}
          />
          {showValidation && (!question.question || question.question.trim() === '') && (
            <ValidationMessage>Please Enter The Question...</ValidationMessage>
          )}
          <Select
            value={question.responseType}
            onChange={handleTypeChange}
            style={{ width: '100%' }}
            size="large"
            disabled={isFormDisabled}
            options={RESPONSE_TYPES.map(t => ({ label: t.label, value: t.value }))}
          />
        </div>
      </QuestionCardHeader>

      {showOptions && (
        <div style={{ paddingLeft: 36, marginBottom: 12 }}>
          {(question.options || []).map((opt, optIdx) => (
            <OptionRow key={optIdx}>
              <OptionInput
                value={opt}
                onChange={(e) => handleOptionChange(optIdx, e.target.value)}
                placeholder={`Option ${optIdx + 1}`}
                disabled={isFormDisabled}
              />
              {!isFormDisabled && question.options.length > 1 && (
                <IconButton onClick={() => handleRemoveOption(optIdx)}>
                  <CloseOutlined style={{ fontSize: 14, color: '#999FAC' }} />
                </IconButton>
              )}
            </OptionRow>
          ))}
          {showOptionsError && (
            <ValidationMessage>
              {getResponseTypeLabel(question.responseType)} requires at least one option. Add an option or change the response type.
            </ValidationMessage>
          )}
          {!isFormDisabled && (
            <AddOptionButton onClick={handleAddOption}>
              <PlusOutlined style={{ fontSize: 12 }} />
              Add Option
            </AddOptionButton>
          )}
        </div>
      )}

      <CardFooter>
        <CardActionsLeft>
          {isCustom && (
            <>
              <IconButton onClick={onDelete} disabled={isFormDisabled}>
                <DeleteOutlined style={{ fontSize: 18, color: '#999FAC' }} />
              </IconButton>
              <IconButton onClick={onDuplicate} disabled={isFormDisabled}>
                <CopyOutlined style={{ fontSize: 18, color: '#999FAC' }} />
              </IconButton>
            </>
          )}
        </CardActionsLeft>
        <RequiredToggle>
          <Switch
            checked={question.required}
            onChange={handleRequiredToggle}
            size="small"
            disabled={isFormDisabled}
          />
          <RequiredLabel>Required</RequiredLabel>
        </RequiredToggle>
      </CardFooter>
    </QuestionCardWrapper>
  )
}

export default QuestionCard
